import React, { useEffect, useState, useCallback } from 'react';
import {
  HiOutlineMagnifyingGlass,
  HiOutlineArrowPath,
  HiOutlineArrowDownTray,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineFunnel,
} from 'react-icons/hi2';
import sparePartsService from '../services/sparePartsService';
import PageLoading from '../components/common/PageLoading';
import './InventoryHistory.css';

const MOVE_TYPES = ['All', 'Stock In', 'Stock Out', 'Adjustment'];
const PAGE_SIZES = [10, 20, 50, 100];

const MovementBadge = ({ type }) => {
  const map = {
    // New table labels
    'Stock In': { cls: 'badge-in', label: '↑ Stock In' },
    'Stock Out': { cls: 'badge-out', label: '↓ Stock Out' },
    'Adjustment': { cls: 'badge-adj', label: '↔ Adjustment' },
    // Legacy labels from SpareStockTransactions fallback
    'Purchase': { cls: 'badge-in', label: '↑ Purchase' },
    'Issue': { cls: 'badge-out', label: '↓ Issue' },
  };
  const info = map[type] || { cls: 'badge-adj', label: type };
  return <span className={`inv-hist-badge ${info.cls}`}>{info.label}</span>;
};

const InventoryHistory = () => {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [parts, setParts] = useState([]); // for part dropdown

  // Filters
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedPart, setSelectedPart] = useState('');
  const [movementType, setMovementType] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        partId: selectedPart || undefined,
        movementType: movementType !== 'All' ? movementType : undefined,
        search: search || undefined,
        page,
        pageSize,
      };
      const res = await sparePartsService.getInventoryHistory(params);
      setRows(res?.data || []);
      setTotal(res?.total || 0);
      setTotalPages(res?.totalPages || 1);
    } catch (err) {
      console.error(err);
      // Show empty state instead of error if it's just no data
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      setError('Failed to load inventory history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPart, movementType, search, page, pageSize]);

  // Load parts for dropdown
  useEffect(() => {
    sparePartsService.getParts().then(setParts).catch(() => { });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = () => {
    setPage(1);
    load();
  };

  const exportCSV = () => {
    if (rows.length === 0) return;
    const headers = ['Date', 'Part Number', 'Part Name', 'Type', 'Previous Stock', 'Quantity', 'New Stock', 'User', 'Remarks'];
    const csvRows = [
      headers.join(','),
      ...rows.map(r => [
        new Date(r.transactionDate).toLocaleDateString(),
        r.partNumber,
        `"${r.partName}"`,
        r.transactionType,
        r.previousStock,
        r.quantity,
        r.newStock,
        r.user || 'System',
        `"${r.remarks ?? ''}"`,
      ].join(',')),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory_history_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pagecontainer inv-hist-page">
      {/* Header */}
      <div className="page-header">
        <div className='header-contain'>
          <h1>Inventory History</h1>
          <p>Track all stock movements — purchases, issues, and adjustments</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={load}>
            <HiOutlineArrowPath /> Refresh
          </button>
          <button className="btn btn-primary" onClick={exportCSV}>
            <HiOutlineArrowDownTray /> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="inv-hist-error">{error}</div>}

      {/* Filters Panel */}
      <div className="inv-hist-filters">
        <div className="inv-hist-filter-item">
          <label>From Date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); setPage(1); }}
          />
        </div>
        <div className="inv-hist-filter-item">
          <label>To Date</label>
          <input
            type="date"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); setPage(1); }}
          />
        </div>
        <div className="inv-hist-filter-item">
          <label>Spare Part</label>
          <select value={selectedPart} onChange={e => { setSelectedPart(e.target.value); setPage(1); }}>
            <option value="">All Parts</option>
            {parts.map(p => (
              <option key={p.sparePartId} value={p.sparePartId}>
                {p.partNumber} – {p.partName}
              </option>
            ))}
          </select>
        </div>
        <div className="inv-hist-filter-item">
          <label>Movement Type</label>
          <select value={movementType} onChange={e => { setMovementType(e.target.value); setPage(1); }}>
            {MOVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="inv-hist-filter-item search">
          <label>Search</label>
          <div className="inv-hist-search-wrap">
            <HiOutlineMagnifyingGlass className="inv-hist-search-icon" />
            <input
              type="text"
              placeholder="Part name or number…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
        <div className="inv-hist-filter-item" style={{ alignSelf: 'flex-end', paddingBottom: '4px' }}>
          <button className="inv-hist-btn-filter" onClick={handleFilterChange}>
            <HiOutlineFunnel /> Apply Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="inv-hist-table-container">
        <div className="inv-hist-table-toolbar">
          <span className="inv-hist-count">{total.toLocaleString()} record{total !== 1 ? 's' : ''}</span>
          <div className="inv-hist-page-size-wrap">
            <label>Per page:</label>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="inv-hist-loading">
            <PageLoading variant="compact" message="Loading history…" />
          </div>
        ) : rows.length === 0 ? (
          <div className="inv-hist-empty">
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No transactions found</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
              Record a Purchase, Issue, or Adjustment from the <strong>Spare Parts</strong> page to see history here.
            </div>
          </div>
        ) : (
          <table className="inv-hist-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Part Number</th>
                <th>Part Name</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Prev Stock</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>New Stock</th>
                <th>User</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.transactionId}>
                  <td className="inv-hist-date">
                    {new Date(r.transactionDate).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td>
                    <span className="inv-hist-part-num">{r.partNumber}</span>
                  </td>
                  <td>{r.partName}</td>
                  <td><MovementBadge type={r.transactionType} /></td>
                  <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{Number(r.previousStock).toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{Number(r.quantity).toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--accent-primary)' }}>{Number(r.newStock).toLocaleString()}</td>
                  <td>{r.user || 'System'}</td>
                  <td className="inv-hist-remarks" title={r.remarks}>{r.remarks || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="inv-hist-pagination">
            <button
              className="inv-hist-page-btn"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              <HiOutlineChevronLeft />
            </button>
            <span className="inv-hist-page-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="inv-hist-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <HiOutlineChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryHistory;
